/**
 * QuestionCard Component
 * Reusable component for displaying and editing survey questions
 * Supports multi-language translation via MultiLanguageField
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Star, Type, ThumbsUp, Trash2, ChevronUp, ChevronDown,
    Globe, GripVertical
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedCard, ThemedInput, ThemedButton, ThemedBadge } from '../themed';
import MultiLanguageField from '../common/MultiLanguageField';

/**
 * QuestionCard - Displays and allows editing of a survey question
 *
 * @param {Object} question - Question object
 * @param {number} index - Question index in list
 * @param {number} totalQuestions - Total number of questions
 * @param {string} entityId - Entity ID (null for global questions)
 * @param {boolean} isGlobal - Whether this is a global question
 * @param {boolean} canEdit - Whether user can edit this question
 * @param {boolean} canDelete - Whether user can delete this question
 * @param {boolean} canReorder - Whether user can reorder this question
 * @param {boolean} multiLanguageEnabled - Whether multi-language is enabled
 * @param {string} entityDefaultLanguage - Entity's default language
 * @param {Function} onChange - Callback when question changes
 * @param {Function} onDelete - Callback when question deleted
 * @param {Function} onMove - Callback when question moved (up/down)
 */
const QuestionCard = ({
    question,
    index,
    totalQuestions,
    entityId,
    isGlobal = false,
    canEdit = true,
    canDelete = true,
    canReorder = true,
    multiLanguageEnabled = false,
    entityDefaultLanguage = 'en',
    onChange,
    onDelete,
    onMove
}) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();

    // Get question type icon and label
    const getQuestionTypeInfo = (type) => {
        const types = {
            scale: { icon: Star, label: t('ratingScale'), color: 'text-yellow-500' },
            rating: { icon: Star, label: t('starRating'), color: 'text-yellow-500' },
            text: { icon: Type, label: t('textResponse'), color: 'text-blue-500' },
            yes_no: { icon: ThumbsUp, label: t('yesNo'), color: 'text-green-500' }
        };
        return types[type] || types.text;
    };

    const typeInfo = getQuestionTypeInfo(question.question_type);
    const TypeIcon = typeInfo.icon;

    // Handle field changes
    const handleChange = (field, value) => {
        if (onChange) {
            onChange(index, field, value);
        }
    };

    // Handle translation changes
    const handleTranslationChange = (field, translatedValue) => {
        // Translation is saved separately, just track that it changed
        console.log('Translation updated:', field, translatedValue);
    };

    return (
        <ThemedCard className={cn("p-4", border.default)}>
            <div className="flex items-start gap-3">
                {/* Drag handle (if reorderable) */}
                {canReorder && (
                    <div className="pt-2">
                        <GripVertical className={cn("w-5 h-5 cursor-move", text.secondary)} />
                    </div>
                )}

                {/* Question content */}
                <div className="flex-1 space-y-4">
                    {/* Question header */}
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className={cn(
                                "w-10 h-10 rounded-lg flex items-center justify-center",
                                background.tertiary
                            )}>
                                <TypeIcon className={cn("w-5 h-5", typeInfo.color)} />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className={cn("font-medium", text.primary)}>
                                        {t('question')} {index + 1}
                                    </span>
                                    {isGlobal && (
                                        <ThemedBadge variant="info" size="sm">
                                            <Globe className="w-3 h-3 mr-1" />
                                            {t('global')}
                                        </ThemedBadge>
                                    )}
                                    {question.source_type === 'custom' && (
                                        <ThemedBadge variant="secondary" size="sm">
                                            {t('custom')}
                                        </ThemedBadge>
                                    )}
                                </div>
                                <span className={cn("text-sm", text.secondary)}>
                                    {typeInfo.label}
                                </span>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1">
                            {/* Move up/down */}
                            {canReorder && (
                                <>
                                    <ThemedButton
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onMove && onMove(index, 'up')}
                                        disabled={index === 0}
                                        title={t('moveUp')}
                                    >
                                        <ChevronUp className="w-4 h-4" />
                                    </ThemedButton>
                                    <ThemedButton
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onMove && onMove(index, 'down')}
                                        disabled={index === totalQuestions - 1}
                                        title={t('moveDown')}
                                    >
                                        <ChevronDown className="w-4 h-4" />
                                    </ThemedButton>
                                </>
                            )}

                            {/* Delete */}
                            {canDelete && (
                                <ThemedButton
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onDelete && onDelete(index)}
                                    className="text-red-500 hover:text-red-700"
                                    title={t('delete')}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </ThemedButton>
                            )}
                        </div>
                    </div>

                    {/* Question text field */}
                    {canEdit ? (
                        isGlobal || question.source_type === 'custom' ? (
                            // Multi-language field for global or custom questions
                            <MultiLanguageField
                                entityId={entityId}
                                entityDefaultLanguage={entityDefaultLanguage}
                                multiLanguageEnabled={multiLanguageEnabled}
                                contentType="survey_question"
                                contentId={question.id}
                                fieldName="question_text"
                                defaultValue={question.question_text}
                                onChange={(fieldName, value, isDefault) => {
                                    if (isDefault) {
                                        handleChange('question_text', value);
                                    }
                                }}
                                label={t('questionText')}
                                required
                                isGlobal={isGlobal}
                            />
                        ) : (
                            // Simple input for non-translatable questions
                            <div>
                                <label className={cn("block text-sm font-medium mb-1", text.primary)}>
                                    {t('questionText')}
                                </label>
                                <ThemedInput
                                    value={question.question_text}
                                    onChange={(e) => handleChange('question_text', e.target.value)}
                                    placeholder={t('enterQuestionText')}
                                    required
                                />
                            </div>
                        )
                    ) : (
                        // Read-only display
                        <div>
                            <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                {t('questionText')}
                            </label>
                            <p className={cn("text-base", text.primary)}>
                                {question.question_text}
                            </p>
                        </div>
                    )}

                    {/* Help text field (optional) */}
                    {canEdit ? (
                        isGlobal || question.source_type === 'custom' ? (
                            <MultiLanguageField
                                entityId={entityId}
                                entityDefaultLanguage={entityDefaultLanguage}
                                multiLanguageEnabled={multiLanguageEnabled}
                                contentType="survey_question"
                                contentId={question.id}
                                fieldName="help_text"
                                defaultValue={question.help_text || ''}
                                onChange={(fieldName, value, isDefault) => {
                                    if (isDefault) {
                                        handleChange('help_text', value);
                                    }
                                }}
                                label={t('helpText')}
                                multiline
                                isGlobal={isGlobal}
                            />
                        ) : (
                            <div>
                                <label className={cn("block text-sm font-medium mb-1", text.primary)}>
                                    {t('helpText')} ({t('optional')})
                                </label>
                                <ThemedInput
                                    value={question.help_text || ''}
                                    onChange={(e) => handleChange('help_text', e.target.value)}
                                    placeholder={t('optionalHelpText')}
                                    as="textarea"
                                    rows={2}
                                />
                            </div>
                        )
                    ) : (
                        question.help_text && (
                            <div>
                                <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                                    {t('helpText')}
                                </label>
                                <p className={cn("text-sm", text.secondary)}>
                                    {question.help_text}
                                </p>
                            </div>
                        )
                    )}

                    {/* Required toggle */}
                    {canEdit && (
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id={`required-${index}`}
                                checked={question.is_required ?? true}
                                onChange={(e) => handleChange('is_required', e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label
                                htmlFor={`required-${index}`}
                                className={cn("text-sm font-medium cursor-pointer", text.primary)}
                            >
                                {t('requiredQuestion')}
                            </label>
                        </div>
                    )}

                    {/* Question type selector (for new questions only) */}
                    {canEdit && !question.id && (
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                {t('questionType')}
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { value: 'scale_1_5', label: t('ratingScale'), icon: Star },
                                    { value: 'rating', label: t('starRating'), icon: Star },
                                    { value: 'text', label: t('textResponse'), icon: Type },
                                    { value: 'yes_no', label: t('yesNo'), icon: ThumbsUp }
                                ].map(type => {
                                    const Icon = type.icon;
                                    return (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() => handleChange('question_type', type.value)}
                                            className={cn(
                                                "flex items-center gap-2 p-3 rounded-lg border-2 transition-all",
                                                question.question_type === type.value
                                                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                                    : cn("border-transparent", background.tertiary, "hover:border-gray-300")
                                            )}
                                        >
                                            <Icon className="w-4 h-4" />
                                            <span className={cn("text-sm font-medium", text.primary)}>
                                                {type.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ThemedCard>
    );
};

export default QuestionCard;
